import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Lenovo Group Limited",
  industry: ["Technology & Electronics"],
  sub_industry: ["Personal Computers & Smart Devices"],
  founded_year: 1984,
  founded_original_name: "Legend",
  headquarters: ["Beijing, China", "Morrisville, USA"],
  tagline_primary: "Smarter technology for all",
  mission: '"Smarter technology for all – delivering innovative solutions that empower people everywhere to achieve more."',
  vision: '"To become the global leader in Intelligent Transformation, providing smarter technology for all."',
  overview:
    "Lenovo Group Limited is a Chinese multinational technology company specializing in designing, manufacturing, and marketing consumer electronics, personal computers, software, business solutions, and related services. It is the world's largest personal computer vendor by unit sales.",
  scale: {
    revenue: 69.1,
    employees_estimate: 69500,
    markets_served_count: 4,
    operating_countries_count: 60,
  },
  global_presence: {
    regions: [
      {
        region: "North America",
        headquarters: "Morrisville, North Carolina, USA",
        active_markets: ["United States", "Canada", "Mexico"],
        market_type: "Direct, Owned",
      },
      {
        region: "Asia-Pacific",
        headquarters: "Singapore",
        active_markets: ["Singapore", "Southeast Asia", "Australia", "Japan", "India", "Taiwan", "Hong Kong"],
        market_type: "Direct, Owned",
      },
      {
        region: "EMEA - Europe",
        headquarters: "Paris, France",
        active_markets: ["France", "Germany", "United Kingdom", "Italy", "Spain", "Netherlands", "Western Europe", "Eastern Europe"],
        market_type: "Direct, Owned",
      },
      {
        region: "Middle East and Africa (MEA)",
        headquarters: "Riyadh, Saudi Arabia",
        active_markets: ["Saudi Arabia", "United Arab Emirates", "South Africa", "Kenya", "Nigeria", "Ghana", "Egypt"],
        market_type: "Direct, Owned",
      },
      {
        region: "China (Domestic Market)",
        headquarters: "Beijing, China",
        active_markets: ["Mainland China"],
        market_type: "Direct, Owned",
      },
      {
        region: "Latin America",
        headquarters: "Not found",
        active_markets: ["Brazil", "Argentina", "Mexico", "Chile", "Colombia", "Peru"],
        market_type: "Direct, Owned",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "Americas (North America and Latin America)",
        share_percent: 34,
        notes: "Approximately 34% of revenue in 2023-24, double-digit revenue growth year-on-year in FY24/25",
      },
      {
        region: "China",
        share_percent: 27.5,
        notes: "Approximately 25-30% of revenue, double-digit revenue growth year-on-year in FY24/25",
      },
      {
        region: "EMEA (Europe, Middle East, Africa)",
        share_percent: 26.5,
        notes: "Approximately 25-28% of revenue, double-digit revenue growth year-on-year in FY24/25",
      },
      {
        region: "Asia-Pacific (excluding China)",
        share_percent: 16.5,
        notes: "Approximately 15-18% of revenue, 31% YoY growth in Q4 2024, the highest growth rate among all regions",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        {
          name: "Lenovo Red",
          hex: "#E42022",
        },
        {
          name: "White",
          hex: "#FFFFFF",
        },
      ],
      secondary: [
        {
          name: "Lenovo Blue",
          hex: "#064A89",
        },
        {
          name: "Black",
          hex: "#000000",
        },
        {
          name: "Grey",
          hex: "#808080",
        },
      ],
      accent: [
        {
          name: "Blue",
          hex: "#064A89",
        },
        {
          name: "Green",
          hex: "#008000",
        },
        {
          name: "Orange",
          hex: "#FFA500",
        },
        {
          name: "Pink",
          hex: "#FFC0CB",
        },
      ],
    },
    typography: [
      {
        font_family: "Custom Lenovo typeface (similar to Bunya Bold / Montserrat Alternates Bold)",
        weight: "Bold",
        usage: "Logo, branding, merchandise",
      },
      {
        font_family: "Helvetica / Arial (web-safe sans-serif)",
        weight: "Regular, Bold",
        usage: "Body text, website content",
      },
      {
        font_family: "Sans-serif system fonts",
        weight: "Multiple weights (Regular, Medium, Bold)",
        usage: "Headlines, UI elements",
      },
    ],
    logos: [
      {
        variant: "Primary",
        url: "https://assets.zocket.com/uploads/a0ce4c5f-86a1-4782-a314-471ea5f8fb15-Branding_lenovo-logo_lenovologoposred_low_res.png",
      },
    ],
  },
  verbal_identity: {
    voice_traits: ["Innovative", "Inclusive", "Empowering", "Customer-centric", "Action-oriented", "Trustworthy", "Forward-thinking"],
    taglines: [
      "Smarter Technology for All",
      "New World. New Thinking.",
      "For Those Who Do",
      "We Make The Tools. You Make Them Do",
      "Different is Better",
      "Innovation Never Stands Still",
      "We are building a new technology company",
      "You'll wish everything was a Lenovo",
    ],
    messaging_dos: ["Focus on human benefits, not specs", "Use clear, jargon-free language", "Emphasize innovation and reliability", "Speak inclusively to all users"],
    messaging_donts: ["Avoid overly technical jargon", "Don't use fear-based marketing", "Never disparage competitors", "Avoid exclusionary language"],
    banned_words: ["Cheap", "Basic", "Old-fashioned", "Complicated", "Difficult", "Obsolete", "Slow"],
  },
  creative_assets: [
    "https://assets.zocket.com/uploads/f209603a-da24-42db-8e4b-a3fe05050ce0-600295207_1085802746911775_6999628733028670559_n.jpg",
    "https://assets.zocket.com/uploads/c97c23b1-7af5-48f7-b0ae-71478b9aa77a-609081795_1458365782581519_6304065806747368342_n.jpg",
    "https://assets.zocket.com/uploads/59b9680b-1d47-4011-a41a-ba6ab9d5a718-622605854_889741870598651_678406052631246083_n.jpg",
    "https://assets.zocket.com/uploads/b87be0c4-a04e-4316-b4e6-167565a760ca-623368230_25058220633852299_7492342400051076620_n.jpg",
    "https://assets.zocket.com/uploads/f10a7398-a04e-48e1-8475-571e3da334bc-624278818_884509514170381_4387659222043093464_n.jpg",
    "https://assets.zocket.com/uploads/78cde8f9-4d72-434f-b096-3b019777f699-626538656_1547012813240335_4424912666519488503_n.jpg",
  ],
  compliance: {
    brand_safety_guidelines: ["No political messaging", "No competitor disparagement"],
    copyright_trademark_usage: ["Use ® with brand name"],
  },
  brand_architecture: {
    available_brands: [
      "ThinkPad",
      "ThinkBook",
      "ThinkCentre",
      "IdeaPad",
      "Yoga",
      "Legion",
      "IdeaCentre",
      "Motorola",
      "Motorola Moto",
      "Intelligent Devices Group (IDG)",
      "Infrastructure Solutions Group (ISG)",
      "Solutions and Services Group (SSG)",
    ],
    selected_brand: "Lenovo",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "ThinkPad Product Page",
        description: "Button CTA color deviates from Lenovo blue #064A89",
        severity: "high",
      },
      {
        title: "Legion Gaming Landing Page",
        description: "Heading font size inconsistent with typography scale",
        severity: "medium",
      },
      {
        title: "Social Media Ad - LinkedIn",
        description: "Logo spacing does not meet minimum clearance requirements",
        severity: "low",
      },
      {
        title: "IdeaPad Campaign Banner",
        description: "Secondary color usage exceeds recommended 20% threshold",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Clean Minimalism", score: 94, change: 6 },
      { preference: "Product Focus", score: 89, change: 4 },
      { preference: "Red Accents", score: 86, change: 2 },
      { preference: "Tech Lifestyle", score: 81, change: 7 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 6.5,
    sentiment_graph: [
      { metric: "Sentiment Score", value: "81%", delta: "+3.8%" },
      { metric: "Net Promoter Score", value: "+52", delta: "+6" },
      { metric: "PC Market Share (#1)", value: "24%", delta: "+0.8%" },
      { metric: "Share of Voice", value: "28%", delta: "+2.1%" },
    ],
  },
  sentiment_summary: {
    time_range: ["2024", "2025"],
    themes: {
      positive: [
        "Value for Money & Competitive Pricing",
        "Build Quality & Durability",
        "Performance & Processing Power",
        "Product Range & Customization",
        "Battery Life (ThinkPad series)",
        "Innovation & AI Integration",
        "Market Leadership & Reliability",
      ],
      neutral: ["Battery Power (General)", "Product Longevity Concerns", "Upgrade Intentions", "Regional Support Variations", "Screen Quality (ThinkPad)"],
      negative: [
        "Customer Service & Support Issues",
        "Refund Processing Delays",
        "Hardware Reliability Problems",
        "Warranty & Repair Frustrations",
        "Offshore Support Quality",
        "Budget Line Quality Issues",
        "Delivery & Logistics Problems",
      ],
    },
  },
  distribution: [
    { brand: "Lenovo", share: 24.1 },
    { brand: "HP Inc.", share: 20.2 },
    { brand: "Dell Technologies", share: 15.1 },
    { brand: "Apple", share: 8.7 },
    { brand: "Others", share: 31.9 },
  ],
  sentiment_by_product: [
    { product: "ThinkPad Series", sentiment: 8.2, change_delta: 0.3 },
    { product: "Legion Gaming", sentiment: 7.8, change_delta: 0.5 },
    { product: "Yoga Series", sentiment: 7.5, change_delta: 0 },
    { product: "IdeaPad Series", sentiment: 6.2, change_delta: -0.4 },
    { product: "ThinkBook", sentiment: 7, change_delta: 0.2 },
    { product: "Yoga Pro (Creator)", sentiment: 7.9, change_delta: 0.3 },
    { product: "Legion Slim", sentiment: 8, change_delta: 0.6 },
    { product: "Tablets", sentiment: 6.5, change_delta: -0.2 },
  ],
  top_topics: {
    praise: [
      { topic: "Value for Money", weight: 10 },
      { topic: "Build Quality & Durability", weight: 9 },
      { topic: "Performance & Speed", weight: 9 },
      { topic: "Product Range & Versatility", weight: 8 },
      { topic: "ThinkPad Keyboard Quality", weight: 8 },
      { topic: "Battery Life (Premium Lines)", weight: 7 },
      { topic: "Innovation & Design", weight: 7 },
    ],
    complaints: [
      { topic: "Poor Customer Service", weight: 10 },
      { topic: "Refund Processing Delays", weight: 9 },
      { topic: "Hardware Reliability (Budget Lines)", weight: 9 },
      { topic: "Warranty & Repair Issues", weight: 9 },
      { topic: "Battery Life (General Products)", weight: 7 },
      { topic: "Screen Quality (Select Models)", weight: 7 },
      { topic: "Delivery & Shipping Problems", weight: 7 },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    { name: "HP Inc.", threat_level: "high", market_share: 20.9, sentiment: 71 },
    { name: "Dell Technologies", threat_level: "high", market_share: 15.6, sentiment: 62 },
    { name: "Apple Inc.", threat_level: "high", market_share: 9.2, sentiment: 84 },
    { name: "ASUS", threat_level: "medium", market_share: 7.1, sentiment: 75 },
    { name: "Acer Inc.", threat_level: "medium", market_share: 6.9, sentiment: 68 },
    { name: "Microsoft Corporation", threat_level: "medium", market_share: 3.5, sentiment: 70 },
  ],
  secondary_competitors: [],
  share_of_voice: [
    {
      channel: "Digital/Social Media",
      distribution: { Lenovo: 22.7, HP: 25, Dell: 22.5, Apple: 31.8 },
    },
    {
      channel: "Search (Organic + Paid)",
      distribution: { Lenovo: 27, HP: 28.1, Dell: 23.6, Apple: 21.3 },
    },
    {
      channel: "PR",
      distribution: { Lenovo: 27, HP: 28.1, Dell: 23.6, Apple: 21.3 },
    },
  ],
  positioning: {
    competitive_advantages: ["Broad product portfolio", "Global reach", "Innovation in AI PCs"],
    improvement_areas: ["Strong brand loyalty of competitors", "Integrated ecosystems of competitors"],
    disruption_watchlist: ["Framework Computer", "System76", "Razer Inc.", "Xiaomi Corporation"],
  },
  competitive_benchmarking: [
    {
      metric_name: "Net Trust Quotient Score 2025",
      brands: [
        { brand_name: "Apple", score: 100 },
        { brand_name: "HP", score: 90 },
        { brand_name: "Microsoft", score: 85 },
        { brand_name: "Dell", score: 80 },
        { brand_name: "Lenovo", score: 75 },
        { brand_name: "ASUS", score: 70 },
      ],
    },
    {
      metric_name: "Net Trust Quotient Score 2024",
      brands: [
        { brand_name: "Apple", score: 100 },
        { brand_name: "HP", score: 88 },
        { brand_name: "Microsoft", score: 82 },
        { brand_name: "Dell", score: 78 },
        { brand_name: "Lenovo", score: 72 },
      ],
    },
    {
      metric_name: "Customer Satisfaction Score (CSAT)",
      brands: [
        { brand_name: "Apple", score: 84 },
        { brand_name: "Lenovo", score: 71 },
        { brand_name: "Dell", score: 62 },
        { brand_name: "HP", score: 68 },
        { brand_name: "ASUS", score: 73 },
      ],
    },
    {
      metric_name: "Net Promoter Score (NPS)",
      brands: [
        { brand_name: "Apple", score: 74 },
        { brand_name: "Dell Technologies", score: 58 },
        { brand_name: "Dell", score: 44 },
        { brand_name: "Lenovo", score: 52 },
        { brand_name: "HP", score: 44 },
      ],
    },
    {
      metric_name: "Product Reliability Score",
      brands: [
        { brand_name: "Lenovo ThinkPad", score: 94 },
        { brand_name: "Apple", score: 85 },
        { brand_name: "HP", score: 75 },
        { brand_name: "Dell", score: 70 },
        { brand_name: "ASUS", score: 78 },
      ],
    },
    {
      metric_name: "Market Share 2025",
      brands: [
        { brand_name: "Lenovo", score: 100 },
        { brand_name: "HP", score: 84 },
        { brand_name: "Dell", score: 63 },
        { brand_name: "Apple", score: 38 },
        { brand_name: "ASUS", score: 29 },
        { brand_name: "Acer", score: 29 },
      ],
    },
  ],
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Lenovo targets a diverse audience primarily aged 15-35, with a focus on value-conscious, technology-oriented consumers. Their audience includes business professionals, students, and gamers, with a strong presence in urban and suburban areas. Lenovo's audience is characterized by a high level of education and a median income ranging from $65,000 to $95,000 USD.",
  core_demographics: {
    primary_age_range: "25-34",
    gender_split: {
      male: 53.5,
      female: 46.5,
    },
    income_median_usd: 75000,
    location_type: "Urban",
    demographics_distribution: {
      age: [
        { age_range: "18-24", percentage: 22 },
        { age_range: "25-34", percentage: 32 },
        { age_range: "35-44", percentage: 24 },
        { age_range: "45-54", percentage: 14 },
        { age_range: "55+", percentage: 8 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 52 },
        { education_level: "Some College", percentage: 26 },
        { education_level: "Graduate Degree", percentage: 16 },
        { education_level: "High School", percentage: 6 },
      ],
      geographic: [
        { location: "Urban", percentage: 48 },
        { location: "Suburban", percentage: 38 },
        { location: "Rural", percentage: 14 },
      ],
    },
  },
  occupation_distribution: [
    { occupation: "IT & Technology Professionals", percentage: 28 },
    { occupation: "Students", percentage: 22 },
    { occupation: "Business Professionals", percentage: 20 },
    { occupation: "Creative Professionals", percentage: 12 },
    { occupation: "Gamers / Content Creators", percentage: 18 },
  ],
  interests: {
    top_categories: [
      { category: "Technology & Gadgets", score: 9 },
      { category: "Business & Productivity", score: 8 },
      { category: "Gaming", score: 7 },
      { category: "Education & Learning", score: 8 },
      { category: "Travel", score: 7 },
      { category: "Fitness & Health", score: 6 },
      { category: "Finance & Investment", score: 7 },
      { category: "Design & Creativity", score: 7 },
      { category: "News & Current Events", score: 6 },
      { category: "Sustainability", score: 6 },
    ],
    brand_affinities: [
      { brand: "Microsoft", affinity: 75 },
      { brand: "HP", affinity: 45 },
      { brand: "Dell", affinity: 42 },
      { brand: "Intel", affinity: 70 },
      { brand: "Google", affinity: 65 },
      { brand: "NVIDIA", affinity: 60 },
      { brand: "Apple", affinity: 35 },
      { brand: "ASUS", affinity: 38 },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Channel Partners/Retail": 60,
        "Direct Online (Lenovo.com)": 22.5,
        "B2B Direct Sales": 13.5,
        "Online Marketplaces (Amazon, etc.)": 4,
      },
      purchase_frequency: "0.3-0.5 times per year",
      avg_order_value_usd: 1000,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Problem Recognition",
          description: "Customers acknowledge existence of problem either because they have unmet internal need or in response to external stimuli, such as advertising",
          duration_days: 5,
        },
        {
          stage: 2,
          name: "Information Search & Research",
          description:
            "Consumers start gathering information, relying on external (media content, word of mouth) and internal (previous experiences) sources; thorough information search on internet for complex and expensive product",
          duration_days: 10,
        },
        {
          stage: 3,
          name: "Evaluation of Alternatives",
          description: "Compare Lenovo products with competitors (HP, Dell, Apple); review specifications, pricing, reviews; consider brand reputation",
          duration_days: 7,
        },
        { stage: 4, name: "Purchase Decision", description: "Select specific model, configure options, complete transaction through chosen channel (online, retail, B2B)", duration_days: 2 },
        {
          stage: 5,
          name: "Post-Purchase & Support",
          description: "Order confirmation email with link to order details; return policy allows contact within seven days; online technical support provided",
          duration_days: 0,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: {
        Desktop: 37.5,
        Mobile: 42.5,
        Tablet: 20,
      },
      media_channels: {
        YouTube: 21.8,
        "Google Search/Display": 19.9,
        Facebook: 15.3,
        Instagram: 12.5,
        LinkedIn: 10.2,
        TikTok: 8.3,
        "Twitter/X": 5.6,
        "Email Marketing": 6.5,
      },
    },
  },
  platforms: {
    distribution: {
      YouTube: 21.8,
      "Google Search/Display": 19.9,
      Facebook: 15.3,
      Instagram: 12.5,
      LinkedIn: 10.2,
      TikTok: 8.3,
      "Twitter/X": 5.6,
      "Email Marketing": 6.5,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    { platform: "Instagram (@lenovo)", followers: 1000000, health_score: 85 },
    { platform: "Instagram - Lenovo Legion (@lenovolegion)", followers: 307000, health_score: 70 },
    { platform: "Instagram - Lenovo India (@lenovo_india)", followers: 436000, health_score: 70 },
    { platform: "Facebook", followers: 2500000, health_score: 85 },
    { platform: "Twitter/X (@Lenovo)", followers: 737700, health_score: 85 },
    { platform: "LinkedIn (lenovo)", followers: 988249, health_score: 90 },
    { platform: "TikTok (@lenovo)", followers: 1500000, health_score: 90 },
    { platform: "YouTube (@Lenovo)", followers: 348000, health_score: 85 },
    { platform: "Pinterest (@lenovous)", followers: 7400, health_score: 40 },
  ],
  sentiment_distribution: [
    { label: "Positive", percentage: 40 },
    { label: "Neutral", percentage: 40 },
    { label: "Negative", percentage: 20 },
  ],
  recent_comments: [
    {
      user: "tech_enthusiast",
      platform: "Instagram",
      timeAgo: "2 days ago",
      status: "positive",
      commentText: "Love the new Lenovo Yoga series!",
      postContext: "Post about Lenovo Yoga series launch",
    },
    {
      user: "gamer123",
      platform: "Twitter/X",
      timeAgo: "1 week ago",
      status: "neutral",
      commentText: "Lenovo Legion is decent, but could use more updates.",
      postContext: "Discussion on Lenovo Legion updates",
    },
    {
      user: "business_guru",
      platform: "LinkedIn",
      timeAgo: "3 days ago",
      status: "positive",
      commentText: "Impressed by Lenovo's B2B strategies!",
      postContext: "Post about Lenovo's B2B strategies",
    },
  ],
  engagement: {
    engagement_by_platform: [
      { platform: "TikTok", engagement_rate: 6.8 },
      { platform: "Instagram (Main Account)", engagement_rate: 2 },
      { platform: "Instagram (Lenovo India)", engagement_rate: 5 },
      { platform: "LinkedIn", engagement_rate: 7.8 },
      { platform: "Twitter/X", engagement_rate: 6.5 },
      { platform: "Facebook", engagement_rate: 7 },
      { platform: "YouTube", engagement_rate: 0 },
    ],
    best_performing_content_types: [
      { content_type: "Video Content (General)", performance: 9 },
      { content_type: "TikTok Reels / Short-Form Video", performance: 10 },
      { content_type: "Instagram Reels", performance: 8 },
      { content_type: "LinkedIn Thought Leadership Ads (Video)", performance: 10 },
      { content_type: "LinkedIn Thought Leadership Ads (Single Image)", performance: 9 },
      { content_type: "Real-Time / Trend-Jacking Content", performance: 10 },
      { content_type: "Employee Advocacy Content", performance: 8 },
      { content_type: "Influencer Collaboration Videos", performance: 8 },
    ],
    follower_growth_trend: [
      { month: "August", followers: 1000 },
      { month: "September", followers: 2000 },
      { month: "October", followers: 7650 },
      { month: "November", followers: 1500 },
      { month: "December", followers: 1200 },
    ],
  },
};

export const mBrandIntelligenceLenovo: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
